// Veelgestelde vragen (/veelgestelde-vragen). Antwoorden moeten consistent
// blijven met de werkelijke werking en met de juridische pagina's:
// - voorwaarden (content/legal.ts): annuleren, herroepingsrecht, rol platform;
// - /privacystandaard: k>=10, geen AI, EU-hosting, geen tracking;
// - /terugbetaling (content/mutualiteiten.ts): indicatieve bedragen;
// - werkgeversmodel (docs/onboarding-kit/prijsblad.md): cap -> zelf betalen,
//   geen HR-zichtbaarheid, geen goedkeuringsstap.
// Wordingregels: geen resultaatgaranties, "meestal binnen 48 uur" (geen
// garantie), geen diagnose-/screeningframing, crisis -> 1813/112.

import { Lang } from "./mutualiteiten";

export interface FaqItem {
  id: string;
  q: string;
  a: string[];
}

export interface FaqGroup {
  title: string;
  items: FaqItem[];
}

export const FAQ_CONTENT: Record<Lang, { title: string; intro: string; groups: FaqGroup[] }> = {
  nl: {
    title: "Veelgestelde vragen",
    intro: "De antwoorden hieronder beschrijven hoe Carefunded echt werkt — zonder kleine lettertjes. Staat je vraag er niet bij? Mail support@carefunded.be.",
    groups: [
      {
        title: "Starten",
        items: [
          {
            id: "how",
            q: "Hoe werkt Carefunded precies?",
            a: [
              "Je beantwoordt een paar vragen over waar je aan wilt werken (± 2 minuten). Op basis daarvan stellen we je erkende psychologen voor die passen bij je thema, taal en regio, en bij wie je ziekenfonds terugbetaalt. Je kiest zelf, plant een gratis kennismaking of meteen een sessie, en praat via een beveiligde videoverbinding in je browser.",
              "Hoe we de volgorde van de voorstellen bepalen, staat volledig uitgelegd op de pagina 'Hoe matchen wij?'. Er zijn geen betaalde plaatsingen en er wordt geen artificiële intelligentie gebruikt.",
            ],
          },
          {
            id: "speed",
            q: "Hoe snel kan ik terecht?",
            a: [
              "Meestal kun je binnen 48 uur een eerste afspraak plannen. De exacte snelheid hangt af van de agenda van de psycholoog die je kiest; je ziet de beschikbare momenten meteen bij het boeken.",
            ],
          },
          {
            id: "referral",
            q: "Heb ik een verwijsbrief van mijn huisarts nodig?",
            a: [
              "Nee. Je kunt rechtstreeks een afspraak maken. Een gesprek met je huisarts kan wel waardevol zijn — bijvoorbeeld om samen te bekijken welke hulp bij je situatie past.",
            ],
          },
          {
            id: "intro",
            q: "Wat is de gratis kennismaking?",
            a: [
              "Een kort videogesprek van ongeveer 15 minuten met de psycholoog die we je voorstellen. Je bekijkt samen of het klikt en wat je aanpak zou kunnen zijn. De kennismaking is gratis en volledig vrijblijvend: je beslist daarna zelf of je een sessie boekt.",
            ],
          },
          {
            id: "switch",
            q: "Kan ik van psycholoog veranderen als het niet klikt?",
            a: [
              "Ja, altijd. Een goede klik is belangrijk voor het resultaat van de begeleiding. Je kunt op elk moment opnieuw starten en een andere psycholoog kiezen — daar hoef je niets voor uit te leggen.",
            ],
          },
          {
            id: "deepening",
            q: "Wat zijn verdiepende trajecten?",
            a: [
              "In de zelfhulpomgeving vind je gratis, open trajecten met korte lessen en oefeningen die je meteen kunt volgen. Daarnaast zijn er verdiepende trajecten rond thema's als zelfcompassie, grenzen stellen, rouw en communicatie. Die worden door je psycholoog of therapeut toegewezen wanneer een begeleiding gestart is — zo sluiten ze aan bij jouw traject en het tempo van de sessies.",
              "Je notities en antwoorden in een traject zijn altijd privé en worden nooit gedeeld met je psycholoog. Zelfhulp is algemene informatie en geen vervanging voor professionele hulp.",
            ],
          },
          {
            id: "reflection",
            q: "Wat zijn reflectie-vragenlijsten?",
            a: [
              "Reflectie-vragenlijsten zijn korte zelfrapportage-vragenlijsten voor in je begeleiding. Je psycholoog of therapeut wijst er een toe wanneer een begeleiding gestart is; je vult ze in als voorbereiding en bespreekt je antwoorden samen tijdens de sessies.",
              "Het platform berekent geen score en geeft geen beoordeling of interpretatie — je ziet na het invullen alleen je eigen antwoorden, die je kunt afdrukken of meenemen. Je antwoorden verlaten je browser niet en worden nergens bewaard: ook je psycholoog kan ze niet via het platform inkijken. Jij kiest zelf wat je deelt in het gesprek.",
            ],
          },
          {
            id: "crisis",
            q: "Voor welke situaties is Carefunded niet geschikt?",
            a: [
              "Carefunded is geen nooddienst en biedt geen crisishulp. Denk je aan zelfdoding of is er acuut gevaar? Bel dan nu 1813 (Zelfmoordlijn, gratis en 24/7) of 112. Praten kan ook anoniem bij Tele-Onthaal op 106.",
              "Ook voor situaties die intensieve of gespecialiseerde zorg vragen, is een gesprek met je huisarts of een gespecialiseerde dienst vaak de betere eerste stap.",
            ],
          },
        ],
      },
      {
        title: "Kosten & terugbetaling",
        items: [
          {
            id: "price",
            q: "Hoeveel kost een sessie?",
            a: [
              "Elke psycholoog bepaalt zelf zijn of haar tarief; coaching kost gemiddeld € 37,50 per sessie, therapie meer. Je ziet het exacte tarief altijd vóór je boekt, en alle getoonde prijzen zijn totaalprijzen — er komen geen kosten bovenop.",
              "De kennismaking van ± 15 minuten is altijd gratis.",
            ],
          },
          {
            id: "reimb",
            q: "Hoeveel betaalt mijn ziekenfonds terug?",
            a: [
              "Dat verschilt per ziekenfonds. De meeste Vlaamse ziekenfondsen betalen via hun aanvullende verzekering een deel terug — indicatief € 10 tot € 20 per sessie, met een maximum aantal sessies per jaar. In de intake tonen we meteen wat voor jouw ziekenfonds geldt en wat je netto betaalt.",
              "Op de pagina 'Terugbetaling' vind je de indicatieve bedragen per ziekenfonds, met de datum waarop we ze voor het laatst hebben gecontroleerd. De bedragen wijzigen jaarlijks — controleer het actuele bedrag altijd bij je eigen ziekenfonds.",
            ],
          },
          {
            id: "attest",
            q: "Hoe krijg ik mijn terugbetaling — moet ik zelf iets doen?",
            a: [
              "Na een betaalde sessie kun je een attest downloaden. Dat dien je in bij je ziekenfonds, meestal digitaal via hun app of website. Het ziekenfonds stort de tegemoetkoming daarna op je rekening.",
              "Carefunded regelt de terugbetaling niet automatisch en kan ze niet garanderen — of en hoeveel je terugkrijgt, hangt af van je ziekenfonds en je persoonlijke situatie.",
            ],
          },
          {
            id: "conventie",
            q: "Vallen de sessies onder de RIZIV-conventie (eerstelijnspsychologische zorg)?",
            a: [
              "Nee. De federale eerstelijnspsychologische zorg (met lage remgelden via GGZ-netwerken) is een apart systeem met eigen voorwaarden en wachttijden. De sessies op Carefunded zijn niet RIZIV-geconventioneerd; een eventuele terugbetaling verloopt via de aanvullende verzekering van je ziekenfonds.",
            ],
          },
          {
            id: "cancel",
            q: "Hoe annuleer of verzet ik een afspraak?",
            a: [
              "Via de afspraakpagina in je account. Annuleer je tijdig, dan betalen we een al betaalde sessie volledig terug. Annuleer je laattijdig of kom je niet opdagen, dan kan de sessie (gedeeltelijk) worden aangerekend volgens de annuleringsvoorwaarden die je bij het boeken te zien krijgt.",
              "Als consument heb je daarnaast een wettelijk herroepingsrecht van 14 dagen; de details staan in de algemene voorwaarden.",
            ],
          },
        ],
      },
      {
        title: "Kwaliteit & vertrouwelijkheid",
        items: [
          {
            id: "vetting",
            q: "Hoe worden de psychologen geselecteerd?",
            a: [
              "Elke psycholoog wordt vóór activatie door ons team geverifieerd: we controleren het FOD-visum of de erkenning in het officiële eCad-register van de FOD Volksgezondheid, het diploma en de erkenningsdocumenten, en waar relevant de inschrijving bij de Psychologencommissie of het lidmaatschap van een erkende beroepsvereniging. Pas na volledige verificatie — en een kort gesprek — wordt een profiel zichtbaar.",
              "Wijzigt een psycholoog nadien zijn of haar erkenningsgegevens, dan wordt het profiel automatisch opnieuw gecontroleerd. Lees het volledige verhaal op de pagina 'Kwaliteit & erkenning'.",
            ],
          },
          {
            id: "confidential",
            q: "Is wat ik vertel vertrouwelijk?",
            a: [
              "Ja. Je gesprekken vinden plaats met een zelfstandige, erkende psycholoog die gebonden is aan het beroepsgeheim. Carefunded bewaart geen klinische inhoud: videogesprekken worden niet opgenomen en er worden geen sessienotities op het platform bijgehouden.",
              "Je persoonsgegevens verwerken we volgens de AVG, met hosting binnen de EU, zonder advertentie- of trackingcookies. Gezondheidsgegevens verwerken we enkel met je uitdrukkelijke toestemming. Alles daarover staat in ons privacybeleid en onze privacystandaard.",
            ],
          },
          {
            id: "online",
            q: "Werkt online therapie even goed als in een praktijk?",
            a: [
              "Onderzoek wijst uit dat begeleiding via video voor veel mensen en veel hulpvragen goed werkt, en vergelijkbare resultaten kan geven als gesprekken in een praktijkruimte. Het hangt ook af van je persoonlijke voorkeur: sommige mensen praten net makkelijker vanuit hun eigen vertrouwde omgeving. In de gratis kennismaking kun je aftoetsen of het formaat bij je past.",
              "Carefunded geeft geen garanties over een specifiek behandelresultaat — dat kan geen enkele zorgvorm.",
            ],
          },
          {
            id: "notes",
            q: "Wat gebeurt er met mijn gegevens als ik stop?",
            a: [
              "Je kunt je account op elk moment zelf verwijderen via 'Mijn gegevens'. Je persoonsgegevens worden dan verwijderd en afspraken worden geanonimiseerd. Via dezelfde pagina kun je ook al je gegevens downloaden of je toestemmingen intrekken.",
            ],
          },
        ],
      },
      {
        title: "Via je werkgever",
        items: [
          {
            id: "employer-visibility",
            q: "Ziet mijn werkgever dat ik Carefunded gebruik?",
            a: [
              "Nee. Je werkgever ziet nooit wie zich aansluit, wie sessies boekt of waarover die gaan. Werkgevers krijgen uitsluitend geaggregeerde cijfers te zien (totalen en gemiddelden), en pas vanaf 10 personen per groep — daaronder tonen we niets, zelfs geen aantallen. Die regel is technisch ingebouwd en publiek vastgelegd in onze privacystandaard.",
            ],
          },
          {
            id: "employer-cap",
            q: "Wat gebeurt er als mijn gedekte sessies op zijn?",
            a: [
              "Je werkgever financiert sessies tot een afgesproken maximum per jaar (de 'cap'). Zit je aan dat maximum, dan schakelt het platform automatisch over op zelf betalen — met terugbetaling via je ziekenfonds waar van toepassing. Er is bewust geen goedkeuringsstap bij HR: zo kan niemand bij je werkgever individueel zorggebruik afleiden.",
            ],
          },
        ],
      },
    ],
  },
  fr: {
    title: "Questions fréquentes",
    intro: "Les réponses ci-dessous décrivent le fonctionnement réel de Carefunded — sans petits caractères. Votre question n'y figure pas ? Écrivez à support@carefunded.be.",
    groups: [
      {
        title: "Commencer",
        items: [
          {
            id: "how",
            q: "Comment fonctionne Carefunded exactement ?",
            a: [
              "Vous répondez à quelques questions sur ce que vous souhaitez travailler (± 2 minutes). Sur cette base, nous vous proposons des psychologues agréés qui correspondent à votre thème, votre langue et votre région, et pour lesquels votre mutualité intervient. Vous choisissez vous-même, planifiez une première rencontre gratuite ou directement une séance, et parlez via une connexion vidéo sécurisée dans votre navigateur.",
              "La manière dont nous déterminons l'ordre des propositions est expliquée intégralement sur la page « Comment matchons-nous ? ». Il n'y a pas de placements payants et aucune intelligence artificielle n'est utilisée.",
            ],
          },
          {
            id: "speed",
            q: "Quand puis-je avoir un rendez-vous ?",
            a: [
              "La plupart du temps, vous pouvez planifier un premier rendez-vous dans les 48 heures. Le délai exact dépend de l'agenda du psychologue que vous choisissez ; vous voyez les créneaux disponibles directement lors de la réservation.",
            ],
          },
          {
            id: "referral",
            q: "Ai-je besoin d'une lettre de renvoi de mon médecin généraliste ?",
            a: [
              "Non. Vous pouvez prendre rendez-vous directement. Un entretien avec votre médecin généraliste peut toutefois être utile — par exemple pour examiner ensemble quelle aide convient à votre situation.",
            ],
          },
          {
            id: "intro",
            q: "Qu'est-ce que la première rencontre gratuite ?",
            a: [
              "Un court entretien vidéo d'environ 15 minutes avec le psychologue que nous vous proposons. Vous voyez ensemble si le courant passe et quelle pourrait être l'approche. Cette rencontre est gratuite et sans engagement : vous décidez ensuite vous-même de réserver ou non une séance.",
            ],
          },
          {
            id: "switch",
            q: "Puis-je changer de psychologue si le courant ne passe pas ?",
            a: [
              "Oui, toujours. Une bonne relation de confiance est essentielle. Vous pouvez recommencer à tout moment et choisir un autre psychologue — sans devoir vous justifier.",
            ],
          },
          {
            id: "deepening",
            q: "Que sont les parcours d'approfondissement ?",
            a: [
              "Dans l'espace d'auto-assistance, vous trouvez des parcours ouverts et gratuits, avec de courtes leçons et exercices, que vous pouvez suivre immédiatement. Il existe en outre des parcours d'approfondissement autour de thèmes comme l'auto-compassion, poser des limites, le deuil et la communication. Ils sont attribués par votre psychologue ou thérapeute une fois un accompagnement entamé — ainsi, ils s'inscrivent dans votre parcours et le rythme des séances.",
              "Vos notes et réponses dans un parcours restent toujours privées et ne sont jamais partagées avec votre psychologue. L'auto-assistance est une information générale et ne remplace pas une aide professionnelle.",
            ],
          },
          {
            id: "reflection",
            q: "Que sont les questionnaires de réflexion ?",
            a: [
              "Les questionnaires de réflexion sont de courts questionnaires d'auto-évaluation pour votre accompagnement. Votre psychologue ou thérapeute en attribue un lorsqu'un accompagnement est entamé ; vous le remplissez en préparation et discutez de vos réponses ensemble pendant les séances.",
              "La plateforme ne calcule aucun score et ne donne ni évaluation ni interprétation — après avoir répondu, vous ne voyez que vos propres réponses, que vous pouvez imprimer ou apporter. Vos réponses ne quittent pas votre navigateur et ne sont conservées nulle part : votre psychologue non plus ne peut pas les consulter via la plateforme. Vous choisissez vous-même ce que vous partagez pendant l'entretien.",
            ],
          },
          {
            id: "crisis",
            q: "Dans quelles situations Carefunded ne convient-il pas ?",
            a: [
              "Carefunded n'est pas un service d'urgence et n'offre pas d'aide de crise. Vous pensez au suicide ou il y a un danger immédiat ? Appelez maintenant le Centre de prévention du suicide (0800 32 123, gratuit et 24/7), le 1813 (Zelfmoordlijn) ou le 112. Vous pouvez aussi parler anonymement à Télé-Accueil au 107.",
              "Pour les situations qui demandent des soins intensifs ou spécialisés, un entretien avec votre médecin généraliste ou un service spécialisé est souvent la meilleure première étape.",
            ],
          },
        ],
      },
      {
        title: "Coûts & remboursement",
        items: [
          {
            id: "price",
            q: "Combien coûte une séance ?",
            a: [
              "Chaque psychologue fixe son propre tarif ; le coaching coûte en moyenne 37,50 € par séance, la thérapie davantage. Vous voyez toujours le tarif exact avant de réserver, et tous les prix affichés sont des prix totaux — aucun frais ne s'y ajoute.",
              "La première rencontre de ± 15 minutes est toujours gratuite.",
            ],
          },
          {
            id: "reimb",
            q: "Combien ma mutualité rembourse-t-elle ?",
            a: [
              "Cela varie selon la mutualité. La plupart des mutualités interviennent via leur assurance complémentaire — à titre indicatif 10 à 20 € par séance, avec un nombre maximal de séances par an. Lors de l'intake, nous montrons immédiatement ce qui s'applique à votre mutualité et ce que vous payez net.",
              "Sur la page « Remboursement », vous trouvez les montants indicatifs par mutualité, avec la date de notre dernière vérification. Les montants changent chaque année — vérifiez toujours le montant actuel auprès de votre propre mutualité.",
            ],
          },
          {
            id: "attest",
            q: "Comment obtenir mon remboursement — dois-je faire quelque chose ?",
            a: [
              "Après une séance payée, vous pouvez télécharger une attestation. Vous l'introduisez auprès de votre mutualité, généralement via son application ou son site web. La mutualité verse ensuite l'intervention sur votre compte.",
              "Carefunded ne gère pas le remboursement automatiquement et ne peut pas le garantir — le montant éventuel dépend de votre mutualité et de votre situation personnelle.",
            ],
          },
          {
            id: "conventie",
            q: "Les séances relèvent-elles de la convention INAMI (soins psychologiques de première ligne) ?",
            a: [
              "Non. Les soins psychologiques de première ligne fédéraux (avec de faibles tickets modérateurs via les réseaux de santé mentale) constituent un système distinct, avec ses propres conditions et délais d'attente. Les séances sur Carefunded ne sont pas conventionnées INAMI ; un remboursement éventuel passe par l'assurance complémentaire de votre mutualité.",
            ],
          },
          {
            id: "cancel",
            q: "Comment annuler ou déplacer un rendez-vous ?",
            a: [
              "Via la page du rendez-vous dans votre compte. Si vous annulez à temps, une séance déjà payée est intégralement remboursée. En cas d'annulation tardive ou d'absence, la séance peut être (partiellement) facturée selon les conditions d'annulation affichées lors de la réservation.",
              "En tant que consommateur, vous disposez en outre d'un droit de rétractation légal de 14 jours ; les détails figurent dans les conditions générales.",
            ],
          },
        ],
      },
      {
        title: "Qualité & confidentialité",
        items: [
          {
            id: "vetting",
            q: "Comment les psychologues sont-ils sélectionnés ?",
            a: [
              "Chaque psychologue est vérifié par notre équipe avant activation : nous contrôlons le visa ou l'agrément SPF dans le registre officiel eCad du SPF Santé publique, le diplôme et les documents d'agrément, et le cas échéant l'inscription à la Commission des Psychologues ou l'affiliation à une association professionnelle reconnue. Un profil ne devient visible qu'après vérification complète — et un bref entretien.",
              "Si un psychologue modifie ensuite ses données d'agrément, le profil est automatiquement revérifié. Lisez l'histoire complète sur la page « Qualité & agrément ».",
            ],
          },
          {
            id: "confidential",
            q: "Ce que je raconte est-il confidentiel ?",
            a: [
              "Oui. Vos entretiens ont lieu avec un psychologue agréé indépendant, tenu au secret professionnel. Carefunded ne conserve aucun contenu clinique : les appels vidéo ne sont pas enregistrés et aucune note de séance n'est conservée sur la plateforme.",
              "Nous traitons vos données personnelles conformément au RGPD, avec un hébergement dans l'UE, sans cookies publicitaires ni de suivi. Les données de santé ne sont traitées qu'avec votre consentement explicite. Tout cela figure dans notre politique de confidentialité et notre standard de confidentialité.",
            ],
          },
          {
            id: "online",
            q: "La thérapie en ligne fonctionne-t-elle aussi bien qu'en cabinet ?",
            a: [
              "La recherche montre que l'accompagnement par vidéo fonctionne bien pour beaucoup de personnes et de demandes, et peut donner des résultats comparables aux entretiens en cabinet. Cela dépend aussi de vos préférences : certaines personnes parlent plus facilement depuis leur environnement familier. La première rencontre gratuite permet de vérifier si le format vous convient.",
              "Carefunded ne garantit aucun résultat de traitement spécifique — aucune forme de soins ne le peut.",
            ],
          },
          {
            id: "notes",
            q: "Qu'advient-il de mes données si j'arrête ?",
            a: [
              "Vous pouvez supprimer votre compte à tout moment via « Mes données ». Vos données personnelles sont alors supprimées et les rendez-vous sont anonymisés. Via la même page, vous pouvez aussi télécharger toutes vos données ou retirer vos consentements.",
            ],
          },
        ],
      },
      {
        title: "Via votre employeur",
        items: [
          {
            id: "employer-visibility",
            q: "Mon employeur voit-il que j'utilise Carefunded ?",
            a: [
              "Non. Votre employeur ne voit jamais qui s'inscrit, qui réserve des séances ni de quoi elles traitent. Les employeurs ne reçoivent que des chiffres agrégés (totaux et moyennes), et seulement à partir de 10 personnes par groupe — en dessous, nous ne montrons rien, pas même des nombres. Cette règle est intégrée techniquement et publiée dans notre standard de confidentialité.",
            ],
          },
          {
            id: "employer-cap",
            q: "Que se passe-t-il quand mes séances couvertes sont épuisées ?",
            a: [
              "Votre employeur finance les séances jusqu'à un maximum convenu par an (le « cap »). Une fois ce maximum atteint, la plateforme passe automatiquement au paiement par vous-même — avec remboursement via votre mutualité le cas échéant. Il n'y a délibérément aucune étape d'approbation RH : personne chez votre employeur ne peut ainsi déduire une utilisation individuelle.",
            ],
          },
        ],
      },
    ],
  },
  en: {
    title: "Frequently asked questions",
    intro: "The answers below describe how Carefunded actually works — no fine print. Question not listed? Email support@carefunded.be.",
    groups: [
      {
        title: "Getting started",
        items: [
          {
            id: "how",
            q: "How does Carefunded work exactly?",
            a: [
              "You answer a few questions about what you want to work on (± 2 minutes). Based on that, we propose recognised psychologists who match your topic, language and region, and for whom your health fund provides reimbursement. You choose, plan a free introduction or a session right away, and talk via a secure video connection in your browser.",
              "How we order the proposals is fully explained on the 'How do we match?' page. There are no paid placements and no artificial intelligence is used.",
            ],
          },
          {
            id: "speed",
            q: "How quickly can I get an appointment?",
            a: [
              "Usually you can plan a first appointment within 48 hours. The exact timing depends on the agenda of the psychologist you choose; you see the available slots directly when booking.",
            ],
          },
          {
            id: "referral",
            q: "Do I need a referral from my GP?",
            a: [
              "No. You can book directly. A conversation with your GP can still be valuable — for instance to explore together what kind of help fits your situation.",
            ],
          },
          {
            id: "intro",
            q: "What is the free introduction?",
            a: [
              "A short video call of about 15 minutes with the psychologist we propose. Together you check whether it clicks and what the approach could be. The introduction is free and entirely without obligation: you decide afterwards whether to book a session.",
            ],
          },
          {
            id: "switch",
            q: "Can I switch psychologists if it doesn't click?",
            a: [
              "Yes, always. A good match matters for the outcome of the support. You can start again at any time and choose a different psychologist — no explanation needed.",
            ],
          },
          {
            id: "deepening",
            q: "What are deepening programmes?",
            a: [
              "The self-help environment offers free, open programmes with short lessons and exercises you can start right away. In addition there are deepening programmes around themes such as self-compassion, setting boundaries, grief and communication. These are assigned by your psychologist or therapist once support has started — so they fit your care and the pace of the sessions.",
              "Your notes and answers in a programme always remain private and are never shared with your psychologist. Self-help is general information and no substitute for professional help.",
            ],
          },
          {
            id: "reflection",
            q: "What are reflection questionnaires?",
            a: [
              "Reflection questionnaires are short self-report questionnaires for use within your support. Your psychologist or therapist assigns one once support has started; you fill it in as preparation and discuss your answers together during the sessions.",
              "The platform computes no score and gives no assessment or interpretation — after completing it you only see your own answers, which you can print or bring along. Your answers never leave your browser and are stored nowhere: your psychologist cannot view them through the platform either. You decide what you share in the conversation.",
            ],
          },
          {
            id: "crisis",
            q: "When is Carefunded not the right place?",
            a: [
              "Carefunded is not an emergency service and does not offer crisis care. Are you thinking about suicide, or is there immediate danger? Call 1813 (suicide prevention line, free and 24/7) or 112 now. You can also talk anonymously to Tele-Onthaal on 106.",
              "For situations that require intensive or specialised care, talking to your GP or a specialised service is often the better first step.",
            ],
          },
        ],
      },
      {
        title: "Costs & reimbursement",
        items: [
          {
            id: "price",
            q: "How much does a session cost?",
            a: [
              "Each psychologist sets their own rate; coaching costs on average €37.50 per session, therapy more. You always see the exact rate before you book, and all prices shown are total prices — nothing is added on top.",
              "The ± 15-minute introduction is always free.",
            ],
          },
          {
            id: "reimb",
            q: "How much does my health fund reimburse?",
            a: [
              "That differs per Belgian health fund (mutualiteit). Most funds reimburse part of the cost through their supplementary insurance — indicatively €10 to €20 per session, with a yearly maximum. During the intake we immediately show what applies to your fund and what you pay net.",
              "The 'Reimbursement' page lists the indicative amounts per fund, with the date we last verified them. Amounts change yearly — always check the current amount with your own fund.",
            ],
          },
          {
            id: "attest",
            q: "How do I get my reimbursement — do I need to do anything?",
            a: [
              "After a paid session you can download a certificate. You submit it to your health fund, usually digitally via their app or website. The fund then pays the allowance into your account.",
              "Carefunded does not settle the reimbursement automatically and cannot guarantee it — whether and how much you get back depends on your fund and your personal situation.",
            ],
          },
          {
            id: "conventie",
            q: "Are the sessions part of the RIZIV/INAMI convention (first-line psychological care)?",
            a: [
              "No. Belgium's federal first-line psychological care (with low co-payments via mental health networks) is a separate system with its own conditions and waiting times. Sessions on Carefunded are not RIZIV-convention sessions; any reimbursement runs through your health fund's supplementary insurance.",
            ],
          },
          {
            id: "cancel",
            q: "How do I cancel or move an appointment?",
            a: [
              "Via the appointment page in your account. If you cancel in time, an already-paid session is fully refunded. If you cancel late or don't show up, the session may be (partially) charged according to the cancellation conditions shown when booking.",
              "As a consumer you also have a statutory 14-day right of withdrawal; the details are in the terms and conditions.",
            ],
          },
        ],
      },
      {
        title: "Quality & confidentiality",
        items: [
          {
            id: "vetting",
            q: "How are the psychologists selected?",
            a: [
              "Every psychologist is verified by our team before activation: we check the FPS visa or recognition in the official eCad register of the Belgian FPS Public Health, the diploma and recognition documents, and where relevant the registration with the Psychologists' Commission or membership of a recognised professional association. A profile only becomes visible after full verification — and a short interview.",
              "If a psychologist later changes their credentials, the profile is automatically re-verified. Read the full story on the 'Quality & recognition' page.",
            ],
          },
          {
            id: "confidential",
            q: "Is what I share confidential?",
            a: [
              "Yes. Your conversations take place with an independent, recognised psychologist bound by professional secrecy. Carefunded stores no clinical content: video calls are not recorded and no session notes are kept on the platform.",
              "We process your personal data under the GDPR, with hosting inside the EU and no advertising or tracking cookies. Health data is only processed with your explicit consent. All of this is set out in our privacy policy and privacy standard.",
            ],
          },
          {
            id: "online",
            q: "Does online therapy work as well as in-person?",
            a: [
              "Research shows that video-based support works well for many people and many questions, and can deliver results comparable to in-person conversations. It also depends on your preference: some people find it easier to talk from their own familiar environment. The free introduction lets you check whether the format suits you.",
              "Carefunded gives no guarantees about a specific treatment outcome — no form of care can.",
            ],
          },
          {
            id: "notes",
            q: "What happens to my data if I stop?",
            a: [
              "You can delete your account yourself at any time via 'My data'. Your personal data is then deleted and appointments are anonymised. On the same page you can also download all your data or withdraw your consents.",
            ],
          },
        ],
      },
      {
        title: "Through your employer",
        items: [
          {
            id: "employer-visibility",
            q: "Can my employer see that I use Carefunded?",
            a: [
              "No. Your employer never sees who joins, who books sessions or what they are about. Employers only receive aggregated figures (totals and averages), and only from 10 people per group — below that we show nothing, not even counts. This rule is built into the platform and published in our privacy standard.",
            ],
          },
          {
            id: "employer-cap",
            q: "What happens when my covered sessions run out?",
            a: [
              "Your employer funds sessions up to an agreed yearly maximum (the 'cap'). Once you reach it, the platform automatically switches to self-payment — with health fund reimbursement where applicable. There is deliberately no HR approval step: no one at your employer can infer individual care use.",
            ],
          },
        ],
      },
    ],
  },
};
